import { z } from 'zod';

const plainText = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((value) => !/<\/?[a-z][^>]*>/i.test(value), 'HTML is not allowed')
    .refine((value) => !/(https?:\/\/|www\.)/i.test(value), 'Links are not allowed')
    .refine(
      (value) =>
        !/(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|>\s|```)|!?\[[^\]]*\]\([^)]*\)|[*_~]{2}/.test(value),
      'Markdown is not allowed',
    );

export const NotificationCategory = z.enum(['system', 'task', 'billing', 'marketing']);
export type NotificationCategory = z.infer<typeof NotificationCategory>;

export const NotificationAction = z
  .object({
    type: z.enum(['workbench', 'analysis_task', 'content_draft', 'billing']),
    object_id: z.string().trim().min(1).max(128).optional(),
  })
  .strict()
  .refine((value) => value.type === 'workbench' || value.object_id !== undefined, {
    message: 'object_id is required for this action',
  });
export type NotificationAction = z.infer<typeof NotificationAction>;

export const NotificationTarget = z.discriminatedUnion('type', [
  z.object({ type: z.literal('all') }).strict(),
  z.object({ type: z.literal('tenant'), id: z.string().trim().min(1).max(128) }).strict(),
  z.object({ type: z.literal('user'), id: z.string().trim().min(1).max(128) }).strict(),
]);
export type NotificationTarget = z.infer<typeof NotificationTarget>;

export const NotificationContent = z
  .object({
    category: NotificationCategory,
    title: plainText(80).refine((value) => !value.includes('\n'), 'Title must be one line'),
    body: plainText(500),
    action: NotificationAction.optional(),
  })
  .strict();
export type NotificationContent = z.infer<typeof NotificationContent>;

export const NotificationProductionRequest = NotificationContent.extend({
  tenant_id: z.string().trim().min(1).max(128),
  user_id: z.string().trim().min(1).max(128),
  source_type: z.string().trim().min(1).max(80),
  source_id: z.string().trim().min(1).max(128),
  dedupe_key: z.string().trim().min(1).max(200),
  created_at: z.string().datetime().optional(),
  expires_at: z.string().datetime().optional(),
}).strict();
export type NotificationProductionRequest = z.infer<typeof NotificationProductionRequest>;

export const NotificationListQuery = z.object({
  status: z.enum(['all', 'unread']).default('all'),
  cursor: z.string().trim().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const NotificationItem = z.object({
  id: z.string(),
  category: NotificationCategory,
  source_type: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  action: NotificationAction.nullable(),
  read_at: z.string().nullable(),
  created_at: z.string(),
});
export type NotificationItem = z.infer<typeof NotificationItem>;

export const NotificationListResponse = z.object({
  items: z.array(NotificationItem),
  next_cursor: z.string().nullable(),
});
export type NotificationListResponse = z.infer<typeof NotificationListResponse>;

export const NotificationUnreadCountResponse = z.object({ count: z.number().int().nonnegative() });
export type NotificationUnreadCountResponse = z.infer<typeof NotificationUnreadCountResponse>;

export const NotificationReadResponse = z.object({
  ok: z.literal(true),
  updated: z.number().int(),
});
export type NotificationReadResponse = z.infer<typeof NotificationReadResponse>;

export const AdminNotificationPreviewRequest = z.object({ target: NotificationTarget }).strict();
export const AdminNotificationPreviewResponse = z.object({
  recipient_count: z.number().int().nonnegative(),
});
export type AdminNotificationPreviewResponse = z.infer<typeof AdminNotificationPreviewResponse>;

export const AdminNotificationPublishRequest = NotificationContent.extend({
  target: NotificationTarget,
  confirmed: z.literal(true),
}).strict();
export type AdminNotificationPublishRequest = z.infer<typeof AdminNotificationPublishRequest>;

export const NotificationPublicationStatus = z.enum([
  'queued',
  'processing',
  'completed',
  'completed_with_errors',
  'failed',
]);

export const NotificationPublication = z.object({
  id: z.string(),
  created_by_admin: z.string(),
  target: NotificationTarget,
  category: NotificationCategory,
  title: z.string(),
  body: z.string(),
  action: NotificationAction.nullable(),
  status: NotificationPublicationStatus,
  total_count: z.number().int().nonnegative(),
  success_count: z.number().int().nonnegative(),
  failure_count: z.number().int().nonnegative(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});
export type NotificationPublication = z.infer<typeof NotificationPublication>;

export const NotificationPublicationsResponse = z.object({
  items: z.array(NotificationPublication),
  next_cursor: z.string().nullable(),
});
export type NotificationPublicationsResponse = z.infer<typeof NotificationPublicationsResponse>;

export const NotificationPublishQueueMessage = z.object({
  publication_id: z.string().trim().min(1).max(128),
});
export type NotificationPublishQueueMessage = z.infer<typeof NotificationPublishQueueMessage>;
